import type { AIToolData } from './Types';
import { defaultConfig } from '../../../../../utils/config';

export type ToolViewModel = Pick<AIToolData, 'id' | 'name' | 'status' | 'icon' | 'textInProgress' | 'textCompleted' | 'label' | 'params' | 'startTime' | 'endTime'>;

export type ToolConfig = {
  icon: string;
  textInProgress: string | ((params: any) => string);
  textCompleted: string | ((params: any) => string);
  labelTemplate?: string;
  conditionalText?: {
    condition: (params: any) => boolean;
    ifTrue: { textInProgress: string | ((params: any) => string); textCompleted: string | ((params: any) => string) };
  };
  customLabel?: (params: any, result: any) => string | undefined;
  
  // Debug-only overrides
  debugLabelTemplate?: string;
  debugCustomLabel?: (params: any, result: any) => string | undefined;
  debugConditionalText?: {
    condition: (params: any) => boolean;
    ifTrue: { textInProgress: string | ((params: any) => string); textCompleted: string | ((params: any) => string) };
  };
  
  // Computed methods (always present)
  getInitial: (args: { id: string; name: string; params: any; startTime: number }) => ToolViewModel;
  updateWithResult: (vm: ToolViewModel, result: any) => ToolViewModel;
};

// Template processor for label templates
const processTemplate = (template: string, params: any): string | undefined => {
  if (!template) return undefined;
  
  const getByPath = (obj: any, path: string) => {
    if (!obj) return undefined;
    return path.split('.').reduce((acc: any, k: string) => (acc != null ? acc[k] : undefined), obj);
  };
  
  // Check if all placeholders can be resolved
  const placeholders = Array.from(template.matchAll(/\{([^}]+)\}/g)).map(m => (m[1] || '').trim());
  for (const key of placeholders) {
    const value = getByPath(params, key);
    if (value == null) return undefined;
  }
  
  // Replace placeholders with values
  return template.replace(/\{([^}]+)\}/g, (_match, key) => {
    const value = getByPath(params, String(key).trim());
    return String(value);
  });
};

// Helper functions for common filepath extraction patterns
const extractFilePath = (params: any, result: any): string | undefined => {
  return result?.filePath || params?.args?.filePath || params?.args?.filePaths?.[0] || undefined;
};

const extractFilePathWithCount = (params: any, result: any): { filePath?: string; editCount?: number } => {
  const filePath = extractFilePath(params, result);
  const editCount = result?.editCount || params?.args?.editCount;
  return { filePath, editCount };
};

const extractRenameFilePaths = (params: any, result: any): { oldPath?: string; newPath?: string } => {
  const oldPath = result?.oldPath || params?.args?.oldPath || extractFilePath(params, result);
  const newPath = result?.newPath || params?.args?.newPath;
  return { oldPath, newPath };
};

// Helper functions for art asset name extraction patterns
const extractAssetName = (params: any, result: any): string | undefined => {
  return result?.asset_name || result?.assetName || result?.name || params?.args?.asset_name || params?.args?.assetName || undefined;
};

const extractRenameAssetNames = (params: any, result: any): { oldName?: string; newName?: string } => {
  const oldName = result?.oldName || params?.args?.assetName || undefined;
  const newName = result?.newName || params?.args?.newName || undefined;
  return { oldName, newName };
};

const createTool = (baseConfig: Omit<ToolConfig, 'getInitial' | 'updateWithResult'>): ToolConfig => {
  const resolveText = (text: string | ((params: any) => string), params: any): string => {
    return typeof text === 'function' ? text(params) : text;
  };

  return {
    ...baseConfig,
    textInProgress: typeof baseConfig.textInProgress === 'string' ? baseConfig.textInProgress : 'Running...',
    textCompleted: typeof baseConfig.textCompleted === 'string' ? baseConfig.textCompleted : 'Complete',
    
    getInitial: ({ id, name, params, startTime }) => {
      const isDebug = !!defaultConfig.DEBUG_STREAM_TOOLS;
      const debugConditional = isDebug && baseConfig.debugConditionalText?.condition(params)
        ? baseConfig.debugConditionalText.ifTrue
        : null;
      const normalConditional = !debugConditional && baseConfig.conditionalText?.condition(params)
        ? baseConfig.conditionalText.ifTrue
        : null;

      const textInProgress = debugConditional?.textInProgress
        ? resolveText(debugConditional.textInProgress, params)
        : normalConditional?.textInProgress
          ? resolveText(normalConditional.textInProgress, params)
          : resolveText(baseConfig.textInProgress, params);
      const textCompleted = debugConditional?.textCompleted
        ? resolveText(debugConditional.textCompleted, params)
        : normalConditional?.textCompleted
          ? resolveText(normalConditional.textCompleted, params)
          : resolveText(baseConfig.textCompleted, params);

      return {
        id,
        name,
        status: 'called',
        params: (params && typeof params === 'object' && ('args' in (params as any) || 'meta' in (params as any))) ? params : { args: params },
        startTime,
        endTime: undefined,
        icon: baseConfig.icon,
        textInProgress,
        textCompleted,
        label: undefined
      };
    },
    
    updateWithResult: (vm, result) => {
      const mergedParams = { ...vm.params, result };
      const incomingStatus = (() => {
        try {
          const r: any = result as any;
          const s = r && (r.__status || r.status);
          return typeof s === 'string' ? s : undefined;
        } catch (_) { return undefined; }
      })();
      const nextStatus: 'complete' | 'error' = incomingStatus === 'error' ? 'error' : 'complete';
      const normalLabel = baseConfig.customLabel 
        ? baseConfig.customLabel(vm.params, result)
        : baseConfig.labelTemplate 
          ? processTemplate(baseConfig.labelTemplate, mergedParams)
          : undefined;
      const debugLabel = baseConfig.debugCustomLabel
        ? baseConfig.debugCustomLabel(vm.params, result)
        : (baseConfig.debugLabelTemplate
          ? processTemplate(baseConfig.debugLabelTemplate, mergedParams)
          : undefined);
      const label = (defaultConfig.DEBUG_STREAM_TOOLS && (debugLabel != null)) ? debugLabel : normalLabel;
          
      return {
        ...vm,
        status: nextStatus,
        params: mergedParams,
        label,
        endTime: Date.now()
      };
    }
  };
};

export const TOOL_REGISTRY: Record<string, ToolConfig> = {
  'gather-context': createTool({
    icon: 'page',
    textInProgress: 'Reviewing context...',
    textCompleted: 'Reviewed context',
    // Normal: hide file count (no label)
    customLabel: (params, result) => {
      return undefined;
    },
    // Debug: hide files in CREATE; show for others
    debugCustomLabel: (params, result) => {
      const action = params?.args?.actionType;
      if (action === 'CREATE') return undefined;
      const n = result?.filesCount;
      return typeof n === 'number' ? `${n} files` : undefined;
    },
    conditionalText: {
      condition: (params) => params?.args?.actionType === 'CREATE',
      ifTrue: { textInProgress: 'Selecting template...', textCompleted: 'Selected template' }
    }
  }),

  classify: createTool({
    icon: 'brain',
    textInProgress: 'Classifying...',
    textCompleted: 'Classified',
    customLabel: (params, result) => {
      const role = result?.role || params?.args?.role;
      return role ? String(role) : undefined;
    }
  }),
  
  'gather-codebase': createTool({
    icon: 'code',
    textInProgress: 'Reviewing code...',
    textCompleted: 'Reviewed code',
    // Normal: hide files in CREATE; show for others
    customLabel: (params, result) => {
      const action = params?.args?.actionType;
      if (action === 'CREATE') return undefined;
      const n = result?.filesCount;
      return typeof n === 'number' ? `${n} files` : undefined;
    },
    // Debug: always show files count when available
    debugCustomLabel: (_params, result) => {
      const n = result?.filesCount;
      return typeof n === 'number' ? `${n} files` : undefined;
    },
    conditionalText: {
      condition: (params) => params?.args?.actionType === 'CREATE',
      ifTrue: { textInProgress: 'Selecting template...', textCompleted: 'Selected template' }
    }
  }),
  
  'gather-memory': createTool({
    icon: 'brain',
    textInProgress: 'Finding memories...',
    textCompleted: 'Found',
    customLabel: (_params, result) => {
      const count = result?.conversationsCount;
      if (typeof count !== 'number') return undefined;
      const base = `${count} memories`;
      return result?.isRepeat === true ? `${base} (repeat)` : base;
    }
  }),
  
  'gather-knowledge': createTool({
    icon: 'lightbulb',
    textInProgress: 'Gathering knowledge...',
    textCompleted: 'Gathered',
    labelTemplate: '{result.docsCount} docs'
  }),
  
  'gather-game-art': createTool({
    icon: 'folder',
    textInProgress: (params) => (
      params?.args?.actionType === 'CREATE' ? 'Classifying...' 
      : 'Reviewing art...'
    ),
    textCompleted: (params) => (
      params?.args?.actionType === 'CREATE' ? 'Classified' 
      : 'Reviewed art'
    ),
    customLabel: (params, result) => {
      const action = params?.args?.actionType;
      if (action === 'CREATE') {
        const definedCount = result?.artAssetsCount;
        if (typeof definedCount === 'number' && definedCount > 0) {
          const s = definedCount === 1 ? '' : 's';
          return `${definedCount} Art Asset${s}`;
        }
        if (result?.shouldCreateArtAssets === true) return 'Art';
        return undefined;
      }
      const n = result?.assetsCount;
      return typeof n === 'number' ? `${n} assets` : undefined;
    },
  }),

  plan: createTool({
    icon: 'page',
    textInProgress: 'Planning...',
    textCompleted: 'Created',
    customLabel: (_params, result) => {
      const file = result?.filePath || 'plan.md';
      return file;
    }
  }),

  think: createTool({
    icon: 'brain',
    textInProgress: 'Thinking...',
    textCompleted: 'Thought',
    labelTemplate: 'for {result.durationSec}s'
  }),
  reasoning: createTool({
    icon: 'brain',
    textInProgress: 'Thinking...',
    textCompleted: 'Thought',
    labelTemplate: 'for {result.durationSec}s'
  }),
  
  read_file: createTool({
    icon: 'page',
    textInProgress: 'Reading...',
    textCompleted: 'Read',
    customLabel: (params, result) => extractFilePath(params, result)
  }),
  
  list_files: createTool({
    icon: 'folder',
    textInProgress: 'Listing files...',
    textCompleted: 'Listed',
    labelTemplate: '{result.filesCount} files'
  }),
  
  search_files: createTool({
    icon: 'search',
    textInProgress: 'Searching files...',
    textCompleted: 'Searched',
    customLabel: (_params, result) => {
      const count = result?.filesWithMatches;
      if (typeof count !== 'number') return undefined;
      return count === 0 ? 'files' : `${count} files`;
    }
  }),

  edit_file: createTool({
    icon: 'diff',
    textInProgress: 'Editing...',
    textCompleted: 'Edited',
    customLabel: (params, result) => {
      const { filePath, editCount } = extractFilePathWithCount(params, result);
      if (filePath && typeof editCount === 'number') {
        return editCount >= 2 ? `${filePath} ${editCount}x` : filePath;
      }
      return filePath || undefined;
    }
  }),
  
  write_file: createTool({
    icon: 'edit',
    textInProgress: 'Writing...',
    textCompleted: 'Wrote',
    customLabel: (params, result) => extractFilePath(params, result)
  }),
  
  rename_file: createTool({
    icon: 'arrow-right',
    textInProgress: 'Renaming...',
    textCompleted: 'Renamed',
    customLabel: (params, result) => {
      const { oldPath, newPath } = extractRenameFilePaths(params, result);
      if (oldPath && newPath) {
        return `${oldPath} → ${newPath}`;
      }
      return oldPath || newPath || undefined;
    }
  }),
  
  delete_file: createTool({
    icon: 'trash',
    textInProgress: 'Deleting...',
    textCompleted: 'Deleted',
    customLabel: (params, result) => extractFilePath(params, result)
  }),

  list_art: createTool({
    icon: 'folder',
    textInProgress: 'Listing art...',
    textCompleted: 'Listed',
    labelTemplate: '{result.count} assets'
  }),

  view_art: createTool({
    icon: 'image',
    textInProgress: 'Viewing art...',
    textCompleted: 'Viewed art',
    customLabel: (params, result) => extractAssetName(params, result)
  }),

  delete_art: createTool({
    icon: 'trash',
    textInProgress: 'Deleting art...',
    textCompleted: 'Deleted art',
    customLabel: (params, result) => extractAssetName(params, result) || result?.assetId
  }),

  rename_art: createTool({
    icon: 'arrow-right',
    textInProgress: 'Renaming art...',
    textCompleted: 'Renamed art',
    customLabel: (params, result) => {
      const { oldName, newName } = extractRenameAssetNames(params, result);
      return oldName && newName ? `${oldName} → ${newName}` : undefined;
    }
  }),

  create_art: createTool({
    icon: 'image',
    textInProgress: 'Creating art...',
    textCompleted: 'Created art',
    customLabel: (params, result) => extractAssetName(params, result)
  }),

  update_art: createTool({
    icon: 'image',
    textInProgress: 'Updating art...',
    textCompleted: 'Updated art',
    customLabel: (params, result) => extractAssetName(params, result)
  }),
  
  'split-tasks': createTool({
    icon: 'list',
    textInProgress: 'Defining tasks...',
    textCompleted: 'Defined tasks',
    customLabel: (_params, result) => {
      const rawTasksCount = typeof result?.tasksCount === 'number'
        ? result.tasksCount
        : (Array.isArray(result?.tasks) ? result.tasks.length : undefined);
      const rawSubAgentsCount = typeof result?.count_sub_agents === 'number'
        ? result.count_sub_agents
        : (Array.isArray(result?.subAgents)
          ? result.subAgents.length
          : (Array.isArray(result?.agents) ? result.agents.length : undefined));
      const tasksCount = typeof rawTasksCount === 'number' && rawTasksCount > 0 ? rawTasksCount : undefined;
      const subAgentsCount = typeof rawSubAgentsCount === 'number' && rawSubAgentsCount > 0 ? rawSubAgentsCount : undefined;
      const parts: string[] = [];
      if (typeof tasksCount === 'number') parts.push(`Split ${tasksCount} tasks`);
      if (typeof subAgentsCount === 'number') parts.push(`${subAgentsCount} sub-agents`);
      return parts.length > 0 ? parts.join(', ') : undefined;
    }
  }),

  // ...(defaultConfig.DEBUG_STREAM_TOOLS ? {
    task: createTool({
      icon: 'person',
      textInProgress: 'Sub-agent started...',
      textCompleted: 'Sub-agent done',
      debugCustomLabel: (params, result) => {
        const role = params?.args?.role || result?.role;
        return role ? String(role) : undefined;
      }
    }),
  // } : {}),

  merge_code: createTool({
    icon: 'diff',
    textInProgress: 'Merging changes...',
    textCompleted: 'Merged',
    customLabel: (_params, result) => {
      const files = (result && typeof result === 'object')
        ? ((result as any)?.final?.files ?? (result as any)?.files)
        : undefined;
      if (typeof files === 'number') {
        return `${files} files`;
      }
      return undefined;
    },
    debugCustomLabel: (params, result) => {
      const files = (result && typeof result === 'object')
        ? ((result as any)?.final?.files ?? (result as any)?.files)
        : undefined;
      const conflicts = (result && typeof result === 'object')
        ? (
            (result as any)?.final?.conflicts ??
            (result as any)?.conflicts ??
            (result as any)?.final?.conflictCount ??
            (result as any)?.conflictCount
          )
        : undefined;
      const isFinal = params?.args?.isFinal === true;
      const parts: string[] = [];
      if (typeof files === 'number' && typeof conflicts === 'number') {
        parts.push(`${files} files, ${conflicts} conflicts`);
      } else if (typeof files === 'number') {
        parts.push(`${files} files`);
      } else if (typeof conflicts === 'number') {
        parts.push(`${conflicts} conflicts`);
      }
      if (isFinal) {
        parts.push('final');
      }
      return parts.length > 0 ? parts.join(' ') : undefined;
    }
  }),
  
  lint: createTool({
    icon: 'bug',
    textInProgress: 'Testing...',
    textCompleted: 'Tested',
    // Normal: no label, Debug: show error count or No errors
    debugCustomLabel: (_params, result) => {
      const count = typeof result?.errors?.count === 'number' ? result.errors.count : 0;
      return count >= 1 ? `${count} errors` : 'No errors';
    }
  }),
  
  test: createTool({
    icon: 'bug',
    textInProgress: (params) => {
      const rawType = params?.args?.type;
      const type = rawType ? rawType.charAt(0).toUpperCase() + rawType.slice(1) : '';
      return type ? `${type} testing...` : 'Testing...';
    },
    textCompleted: (params) => {
      const rawType = params?.args?.type;
      const type = rawType ? rawType.charAt(0).toUpperCase() + rawType.slice(1) : '';
      return type ? `${type} tested` : 'Tested';
    },
    // Normal: no label, Debug: show error count or No errors
    debugCustomLabel: (_params, result) => {
      const count = typeof result?.errors?.count === 'number' ? result.errors.count : 0;
      return count >= 1 ? `${count} errors` : 'No errors';
    }
  }),
  
  fix: createTool({
    icon: 'bug',
    textInProgress: 'Fixing...',
    textCompleted: 'Fixed',
    // Normal: no label, Debug: show total fixed or No errors
    debugCustomLabel: (_params, result) => {
      const fixed = typeof result?.totalFixed === 'number' ? result.totalFixed : 0;
      return fixed >= 1 ? `${fixed} errors` : 'No errors';
    }
  }),
};

// Hidden tools: known to the system but intentionally not rendered in the UI
export const HIDDEN_TOOL_NAMES: Set<string> = new Set([
  'stop',
]);

// Normalize a tool name for comparisons (trim, lowercase, hyphen→underscore)
const normalizeToolName = (name: string): string => {
  try {
    return String(name || '').trim().toLowerCase().replace(/-/g, '_');
  } catch (_) {
    return '';
  }
};

// Return canonical candidates for a tool name: base, optional tool_ prefix and _tool suffix variants, plus stripped versions when input already contains the prefix/suffix
const getNormalizedNameCandidates = (name: string): string[] => {
  const base = normalizeToolName(name);
  const set = new Set<string>();
  if (!base) return [];
  set.add(base);
  set.add(`tool_${base}`);
  set.add(`${base}_tool`);
  if (base.startsWith('tool_')) set.add(base.slice(5));
  if (base.endsWith('_tool')) set.add(base.slice(0, -5));
  return Array.from(set);
};

// Given a list of candidate names and a registry, return the first matching key, trying the exact candidate, its hyphen variant, and underscore variant in order.
const resolveFromCandidates = (
  candidates: string[],
  registry: Record<string, unknown>
): string | undefined => {
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    if (registry[c as keyof typeof registry]) return c;
    const hyphen = c.replace(/_/g, '-');
    if (registry[hyphen as keyof typeof registry]) return hyphen;
    const underscore = c.replace(/-/g, '_');
    if (registry[underscore as keyof typeof registry]) return underscore;
  }
  return undefined;
};

export const isHiddenToolName = (name: string): boolean => {
  const candidates = getNormalizedNameCandidates(name);
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    if (HIDDEN_TOOL_NAMES.has(c)) return true;
  }
  return false;
};

// Try to resolve a registry key for a given tool name, tolerating underscore/hyphen variants
const resolveRegistryKey = (name: string): string | undefined => {
  try {
    const raw = String(name || '').trim();
    if (!raw) return undefined;
    const candidates = getNormalizedNameCandidates(raw);
    return resolveFromCandidates(candidates, TOOL_REGISTRY);
  } catch (_) {}
  return undefined;
};

// A tool is considered known if it's in the registry (via resolved key) or if it's a hidden tool
export const isKnownToolName = (name: string): boolean => {
  return !!resolveRegistryKey(name) || isHiddenToolName(name);
};

export const getToolDef = (name: string): ToolConfig => {
  const resolved = resolveRegistryKey(name);
  if (resolved) return TOOL_REGISTRY[resolved];
  // Genuinely unknown tool: when debug enabled, surface as UNKNOWN: <raw name>
  if (defaultConfig.DEBUG_STREAM_TOOLS) {
    const raw = String(name || '');
    return createTool({
      icon: 'warning',
      textInProgress: `UNKNOWN: ${raw}`,
      textCompleted: `UNKNOWN: ${raw}`,
    });
  }
};
